import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import PdfHighlightOverlay, { FieldHighlight } from '../pdf/PdfHighlightOverlay';
import { FieldCoordinate, ComparisonResult } from '../../types/confirmation';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfPreviewPanel.css';

// Configure PDF.js worker - use jsdelivr CDN (more reliable than unpkg)
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfPreviewPanelProps {
  pdfUrl: string | null | undefined;
  loading: boolean;
  // Highlighting props
  selectedField?: string | null;
  fieldCoordinates?: Record<string, FieldCoordinate>;
  comparisonResult?: ComparisonResult;
}

const PdfPreviewPanel: React.FC<PdfPreviewPanelProps> = ({
  pdfUrl,
  loading,
  selectedField,
  fieldCoordinates,
  comparisonResult,
}) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.5);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const pageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize file and options to prevent unnecessary Document reloads
  const pdfFile = useMemo(() => (pdfUrl ? { url: pdfUrl } : null), [pdfUrl]);
  const pdfOptions = useMemo(
    () => ({
      cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
      standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    }),
    []
  );

  // Generate highlights based on selected field and coordinates
  const highlights: FieldHighlight[] = useMemo(() => {
    if (!selectedField || !fieldCoordinates) return [];

    const coords = fieldCoordinates[selectedField];
    if (!coords) return [];

    // Map comparison result to highlight match status
    const matchStatus: 'match' | 'mismatch' | 'pending' =
      comparisonResult === 'match' ? 'match' :
      comparisonResult === 'mismatch' ? 'mismatch' :
      'pending';

    return [{
      fieldName: selectedField,
      page: coords.page,
      coordinates: {
        x: coords.x,
        y: coords.y,
        width: coords.width,
        height: coords.height,
      },
      matchStatus,
    }];
  }, [selectedField, fieldCoordinates, comparisonResult]);

  // Auto-navigate to the page with the highlight and scroll to highlight position
  useEffect(() => {
    if (highlights.length > 0) {
      const targetPage = highlights[0].page;
      if (targetPage !== pageNumber) {
        setPageNumber(targetPage);
      }
      // Scroll to the highlight position within the container after render
      setTimeout(() => {
        if (containerRef.current && pageHeight > 0) {
          const highlight = highlights[0];
          // Calculate the scroll position based on highlight Y coordinate
          // The highlight Y is in PDF coordinates, scale it to rendered size
          const scrollY = highlight.coordinates.y * scale;
          // Center the highlight in the viewport
          const containerHeight = containerRef.current.clientHeight;
          const targetScrollTop = Math.max(0, scrollY - containerHeight / 3);
          containerRef.current.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
        }
      }, 150);
    }
  }, [highlights, pageNumber, pageHeight, scale]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setPdfError(error.message);
  };

  const onPageLoadSuccess = (page: { width: number; height: number }) => {
    setPageWidth(page.width);
    setPageHeight(page.height);
  };

  if (loading) {
    return (
      <div className="pdf-preview-loading">
        <div className="spinner" />
        <p>{t('confirmations.loadingPdf')}</p>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="pdf-preview-empty">
        <p>{t('confirmations.noPdfAvailable')}</p>
      </div>
    );
  }

  if (pdfError) {
    return (
      <div className="pdf-preview-error">
        <p>{t('confirmations.pdfLoadFailed')}</p>
        <p className="error-detail">{pdfError}</p>
      </div>
    );
  }

  return (
    <div className="pdf-preview">
      <div className="pdf-controls">
        {numPages && numPages > 1 && (
          <div className="page-controls">
            <button
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              disabled={pageNumber <= 1}
              className="pdf-nav-btn"
            >
              ←
            </button>
            <span className="page-info">
              {t('confirmations.pageOf', {
                current: pageNumber,
                total: numPages || '?',
              })}
            </span>
            <button
              onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))}
              disabled={pageNumber >= (numPages || 1)}
              className="pdf-nav-btn"
            >
              →
            </button>
          </div>
        )}
        <div className="zoom-controls">
          <button
            onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
            disabled={scale <= 0.5}
            className="pdf-nav-btn"
          >
            −
          </button>
          <span className="zoom-info">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale((s) => Math.min(3, s + 0.25))}
            disabled={scale >= 3}
            className="pdf-nav-btn"
          >
            +
          </button>
        </div>
      </div>
      <div className="pdf-document-container" ref={containerRef}>
        <Document
          file={pdfFile}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="pdf-loading">
              <div className="spinner" />
            </div>
          }
          options={pdfOptions}
        >
          <div ref={pageRef} style={{ position: 'relative' }}>
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              onLoadSuccess={onPageLoadSuccess}
            />
            {pageWidth > 0 && pageHeight > 0 && highlights.length > 0 && (
              <PdfHighlightOverlay
                highlights={highlights}
                currentPage={pageNumber}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
                scale={scale}
              />
            )}
          </div>
        </Document>
      </div>
    </div>
  );
};

export default PdfPreviewPanel;
